import commonApi from "@/api/common"
import { ActionEnum, ResourceTypeEnum, TrackingParams } from "@/api/common/types/Monorail.d"
import stores from "@/stores"

/**
 * 生成符合RFC 4122标准的UUID v4
 */
function generateUUID(): string {
  // 使用crypto.randomUUID() 如果可用（现代浏览器）
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // 回退方案：手动生成符合UUID v4标准的格式
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === "x" ? r : (r & 0x3 | 0x8)

    return v.toString(16)
  })
}

// 应用级别的identity_id，在应用加载时初始化
let sessionIdentityId: string | null = null

// 请求防抖机制，避免短时间内重复请求
const requestCache = new Map<string, Promise<boolean>>()
const DEDUPE_WINDOW = 1000 // 1秒内相同请求只发送一次

/**
 * 获取或生成设备标识符
 * 设备标识符在应用加载时初始化，在当前会话中保持不变
 */
function getOrCreateIdentityId(): string {
  if (!sessionIdentityId) {
    sessionIdentityId = generateUUID()
  }

  return sessionIdentityId
}

/**
 * 检查用户是否为ParcelWILL员工
 */
function isParcelpanelEmployee(): boolean {
  try {
    // 可以通过特定字段判断是否为PP员工，比如从admin后台访问
    return Boolean(window.ParcelWILL?.isFromAdmin || stores.commonStore.authToken)
  } catch (error) {
    console.warn("获取员工状态失败:", error)
    return false
  }
}

/**
 * 获取店铺ID
 */
function getShopId(): number {
  try {
    const { data } = stores.userInfoSore

    return Number(data?.shop_id) || 0
  } catch (error) {
    console.warn("获取店铺ID失败:", error)
    return 0
  }
}

/**
 * 生成请求缓存键
 */
function generateCacheKey(params: TrackingParams): string {
  return `${params.action}-${params.resource_type}-${params.resource_id}-${params.schema_id || "default"}`
}

/**
 * 埋点上报核心函数（优化版本，支持请求防抖）
 */
async function trackEvent(params: TrackingParams): Promise<boolean> {
  const cacheKey = generateCacheKey(params)

  // 检查是否有相同的请求正在进行中
  if (requestCache.has(cacheKey)) {
    console.log("复用进行中的埋点请求:", cacheKey)
    return requestCache.get(cacheKey) as Promise<boolean>
  }

  // 创建新的请求
  const requestPromise = (async () => {
    try {
      const identityId = getOrCreateIdentityId()
      const shopId = getShopId()
      const parcelpanelEmployee = isParcelpanelEmployee()

      const requestData = {
        schema_id: params.schema_id || "default_schema",
        metadata: {
          event_created_at_ms: Date.now(),
        },
        payload: {
          shop_id: shopId,
          parcelpanel_employee: parcelpanelEmployee,
          action: params.action,
          resource_type: params.resource_type,
          resource_id: params.resource_id,
          identity_id: identityId,
        },
      }

      await commonApi.monorailProduce(requestData)
      console.log("埋点上报成功:", requestData)
      return true
    } catch (error) {
      console.error("埋点上报失败:", error)
      return false
    } finally {
      // 延迟清除缓存，避免短时间内重复请求
      setTimeout(() => {
        requestCache.delete(cacheKey)
      }, DEDUPE_WINDOW)
    }
  })()

  // 缓存请求
  requestCache.set(cacheKey, requestPromise)

  return requestPromise
}

/**
 * Monorail埋点追踪器
 */
class MonorailTracker {
  /**
   * 追踪查看事件
   * @param resourceType 资源类型
   * @param resourceId 资源ID，如果是查看应用推荐页面传0
   * @param schemaId 可选的schema_id
   */
  static async trackViewed(
    resourceType: ResourceTypeEnum,
    resourceId: number = 0,
    schemaId?: string,
  ): Promise<boolean> {
    return trackEvent({
      action: ActionEnum.Viewed,
      resource_type: resourceType,
      resource_id: resourceId,
      schema_id: schemaId,
    })
  }

  /**
   * 追踪点击事件
   * @param resourceType 资源类型
   * @param resourceId 资源ID
   * @param schemaId 可选的schema_id
   */
  static async trackClicked(
    resourceType: ResourceTypeEnum,
    resourceId: number,
    schemaId?: string,
  ): Promise<boolean> {
    return trackEvent({
      action: ActionEnum.Clicked,
      resource_type: resourceType,
      resource_id: resourceId,
      schema_id: schemaId,
    })
  }

  /**
   * 追踪推荐应用页面查看
   */
  static async trackRecommendAppPageViewed(schemaId?: string): Promise<boolean> {
    return this.trackViewed(ResourceTypeEnum.RecommendApp, 0, schemaId)
  }

  /**
   * 追踪推荐应用查看
   * @param appId 应用ID
   * @param schemaId
   */
  static async trackRecommendAppViewed(appId: number, schemaId?: string): Promise<boolean> {
    return this.trackViewed(ResourceTypeEnum.RecommendApp, appId, schemaId)
  }

  /**
   * 追踪推荐应用点击
   * @param appId 应用ID
   * @param schemaId
   */
  static async trackRecommendAppClicked(appId: number, schemaId?: string): Promise<boolean> {
    return this.trackClicked(ResourceTypeEnum.RecommendApp, appId, schemaId)
  }

  /**
   * 追踪支持文档查看
   * @param docId 文档ID
   * @param schemaId
   */
  static async trackSupportDocViewed(docId: number, schemaId?: string): Promise<boolean> {
    return this.trackViewed(ResourceTypeEnum.SupportDoc, docId, schemaId)
  }

  /**
   * 追踪支持文档点击
   * @param docId 文档ID
   * @param schemaId
   */
  static async trackSupportDocClicked(docId: number, schemaId?: string): Promise<boolean> {
    return this.trackClicked(ResourceTypeEnum.SupportDoc, docId, schemaId)
  }

  /**
   * 推广合作查看
   * @param PartnerId PartnerId
   * @param schemaId
   */
  static async trackSupportPartnerViewed(PartnerId: number, schemaId?: string): Promise<boolean> {
    return this.trackViewed(ResourceTypeEnum.PARTNERAPP, PartnerId, schemaId)
  }

  /**
   * 推广合作点击
   * @param PartnerId 合作者ID
   * @param schemaId
   */
  static async trackSupportPartnerClicked(PartnerId: number, schemaId?: string): Promise<boolean> {
    return this.trackClicked(ResourceTypeEnum.PARTNERAPP, PartnerId, schemaId)
  }

  /**
   * 获取当前会话标识符
   */
  static getIdentityId(): string {
    return getOrCreateIdentityId()
  }

  /**
   * 重置会话标识符（用于测试或重置）
   */
  static resetIdentityId(): void {
    sessionIdentityId = null
  }

  /**
   * 批量追踪推荐应用查看（优化并发请求）
   * @param appIds 应用ID数组
   * @param schemaId 可选的schema_id
   */
  static async trackRecommendAppsViewed(appIds: number[], schemaId?: string): Promise<boolean[]> {
    const promises = appIds.map(appId =>
      this.trackRecommendAppViewed(appId, schemaId),
    )

    const results = await Promise.allSettled(promises)

    return results.map(result => result.status === "fulfilled" ? result.value : false)
  }

  /**
   * 批量追踪推荐应用点击（优化并发请求）
   * @param appIds 应用ID数组
   * @param schemaId 可选的schema_id
   */
  static async trackRecommendAppsClicked(appIds: number[], schemaId?: string): Promise<boolean[]> {
    const promises = appIds.map(appId =>
      this.trackRecommendAppClicked(appId, schemaId),
    )

    const results = await Promise.allSettled(promises)

    return results.map(result => result.status === "fulfilled" ? result.value : false)
  }
}

export default MonorailTracker
